// retry-policy.ts
//
// Chính sách retry §18. THUẦN: mọi ngưỡng đến từ `RetryConfig` được TIÊM, và
// `now` là tham số — không `Date.now()`, không default số.
//
// ⛔ CẤM literal số ngưỡng trong file này. Gate tĩnh
// test/lib/autopilot/section18-threshold-ast-gate.test.ts duyệt AST và chặn
// ba dạng: `x ?? 3`, `function f(max = 3)`, `const max_counted_failures = 3`.
// Lý do: một số rơi vào code sẽ ÂM THẦM thắng config của user — user sửa
// `~/.avatar/autopilot-config.json` mà hành vi không đổi, không có lỗi nào nổ.
//
// Bốn bộ đếm tách nhau, không gộp làm một:
//   • counted_failures — thất bại "tính điểm" (quota KHÔNG tính, §18: hết hạn
//     ngạch là chuyện của nhà cung cấp, không phải lỗi của ticket);
//   • physical_runs — MỌI lần chạy thật, KỂ CẢ quota, để tên log không trùng;
//   • timeout_count — riêng cho đề xuất tách ticket (quá lâu ⇒ ticket quá to);
//   • infra_retry_count — lỗi hạ tầng, có trần riêng để escalate cho người.
import type { ErrorCategory } from "./error-classifier.js";
import type { BlockedBy } from "./escalation-blocked-by.js";
import { redactEvidence } from "./retry-policy-evidence.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

// Tái xuất để caller chỉ cần biết một module "retry policy" (redact evidence
// nằm ở file con cho vừa giới hạn 200 dòng).
export { redactEvidence } from "./retry-policy-evidence.js";

export type RetryConfig = Pick<
  AutopilotConfig,
  | "max_counted_failures"
  | "split_after_timeouts"
  | "infrastructure_retry_limit"
  | "external_outage_escalate_min"
>;

export interface RetryState {
  counted_failures: number;
  physical_runs: number;
  timeout_count: number;
  infra_retry_count: number;
  outage_started_at?: Date;
}

export interface AttemptOutcome {
  category: ErrorCategory;
  isTimeout?: boolean;
  /** Text thô của lỗi; được redact trước khi vào decision. */
  evidence?: string;
  /** Secret đã biết để redactor bắt được cả biến thể base64/url-encoded. */
  secretValues?: string[];
}

export type RetryOutcome = "retry" | "blocked" | "escalate_waiting_human";

export interface RetryDecision {
  outcome: RetryOutcome;
  state: RetryState;
  reason: string;
  proposeSplit?: boolean;
  evidence?: string;
  /** The triggering attempt's classifier category (§6.4 taxonomy) — carried
   * through so a caller can journal WHY, not just THAT, a ticket became
   * blocked/escalated (dogfooding P9b gap: mission ME-2026-c3985e blocked
   * two tickets with the category nowhere queryable outside process memory). */
  category: ErrorCategory;
  /** Set on every non-retry outcome (orchestrator recovery reads it). */
  blocked_by?: BlockedBy;
}

// Hằng đơn vị, KHÔNG phải ngưỡng §18: đây là số học (đếm +1, đổi ms↔phút),
// không phải chính sách nào user chỉnh được trong autopilot-config.json.
const MS_PER_MINUTE = 60_000;

export function emptyRetryState(): RetryState {
  return {
    counted_failures: 0,
    physical_runs: 0,
    timeout_count: 0,
    infra_retry_count: 0,
  };
}

function minutesBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / MS_PER_MINUTE;
}

/**
 * Ghi nhận kết quả MỘT lần chạy thật, trả state mới + quyết định. Không
 * mutate `state` đầu vào.
 */
export function recordAttemptOutcome(
  state: RetryState,
  outcome: AttemptOutcome,
  config: RetryConfig,
  now: Date,
): RetryDecision {
  const isQuota = outcome.category === "quota" || outcome.category === "rate_limit";
  const isInfra = outcome.category === "network";

  const next: RetryState = {
    ...state,
    // Quota vẫn là một lần chạy thật ⇒ luôn tăng (test 39: attempt_no không
    // được nhảy cóc, nếu không hai lần retry sẽ ghi đè cùng tên file log).
    physical_runs: state.physical_runs + 1,
    counted_failures: isQuota ? state.counted_failures : state.counted_failures + 1,
    timeout_count: outcome.isTimeout === true ? state.timeout_count + 1 : state.timeout_count,
    infra_retry_count: isInfra ? state.infra_retry_count + 1 : state.infra_retry_count,
  };

  const evidence =
    outcome.evidence === undefined
      ? undefined
      : redactEvidence(outcome.evidence, outcome.secretValues ?? []);

  const proposeSplit = next.timeout_count === config.split_after_timeouts ? true : undefined;

  const outageMinutes =
    state.outage_started_at === undefined
      ? undefined
      : minutesBetween(state.outage_started_at, now);
  if (outageMinutes !== undefined && outageMinutes >= config.external_outage_escalate_min) {
    return {
      outcome: "escalate_waiting_human",
      state: next,
      reason: `external outage kéo dài ${outageMinutes} phút, vượt external_outage_escalate_min=${config.external_outage_escalate_min}`,
      proposeSplit,
      evidence,
      category: outcome.category,
      blocked_by: "outage",
    };
  }

  if (isInfra && next.infra_retry_count >= config.infrastructure_retry_limit) {
    return {
      outcome: "escalate_waiting_human",
      state: next,
      reason: `lỗi hạ tầng lần ${next.infra_retry_count}, chạm infrastructure_retry_limit=${config.infrastructure_retry_limit}`,
      proposeSplit,
      evidence,
      category: outcome.category,
      blocked_by: "infra",
    };
  }

  // SOL-06 (audit 2026-09-28): splitting is a HUMAN call (pack escalation.md
  // "chia ticket trước khi chạy thêm"); the mechanical "phần 1/2" copy only
  // duplicated work and was never even created (draft lacked `revision`).
  if (proposeSplit === true) {
    const reason =
      `timeout lần ${next.timeout_count} chạm split_after_timeouts=${config.split_after_timeouts} — ` +
      "ticket quá lớn, cần người chia nhỏ (redirect/plan) trước khi chạy thêm";
    return {
      outcome: "escalate_waiting_human",
      state: next,
      reason,
      proposeSplit,
      // Journal + operator line read `evidence` — lead with WHY a person is needed.
      evidence: evidence === undefined ? reason : `${reason}; ${evidence}`,
      category: outcome.category,
      blocked_by: "timeout_split",
    };
  }

  if (next.counted_failures >= config.max_counted_failures) {
    return {
      outcome: "blocked",
      state: next,
      reason: `counted_failures=${next.counted_failures} chạm max_counted_failures=${config.max_counted_failures}`,
      proposeSplit,
      evidence,
      category: outcome.category,
      blocked_by: "counted_cap",
    };
  }

  return {
    outcome: "retry",
    state: next,
    reason: isQuota
      ? `quota/rate-limit: không tính counted_failures (physical_runs=${next.physical_runs})`
      : `counted_failures=${next.counted_failures}/${config.max_counted_failures}`,
    proposeSplit,
    evidence,
    category: outcome.category,
  };
}
